import { useCallback, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import { facilityDisplayName } from 'utils/campusLabels';
import {
  CreatedByFacilityOption,
  CreatedByFacilityRequest,
  resolveCreatedByFacility
} from 'utils/createdByFacility';

export type CreatedByFacilityChoice =
  { status: 'chosen'; facilityId: string | null } | { status: 'cancelled' };

type ChooseRequest = CreatedByFacilityRequest & {
  /** What is being created, for the prompt's wording. */
  noun?: 'move' | 'task';
  /** One line naming it, e.g. "Trailer JBHZ109588 · NPL Spot 16 → Allentown D27". */
  summary?: string;
};

type Pending = {
  options: CreatedByFacilityOption[];
  defaultId: string;
  noun: 'move' | 'task';
  summary?: string;
};

/**
 * Ask which facility a task from a campus-wide screen is created for, when the user has
 * more than one in the campus and nothing on the page implies one. Render
 * `createdByFacilityDialog` once in the component that calls `chooseCreatedByFacility`.
 */
export function useCreatedByFacility() {
  const [pending, setPending] = useState<Pending | null>(null);
  const resolverRef = useRef<((choice: CreatedByFacilityChoice) => void) | null>(null);

  const settle = useCallback((choice: CreatedByFacilityChoice) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setPending(null);
    resolve?.(choice);
  }, []);

  const chooseCreatedByFacility = useCallback(
    ({
      noun = 'move',
      summary,
      ...request
    }: ChooseRequest): Promise<CreatedByFacilityChoice> => {
      const resolution = resolveCreatedByFacility(request);
      if (resolution.kind === 'implicit') {
        return Promise.resolve({ status: 'chosen', facilityId: resolution.facilityId });
      }

      // A second request while a prompt is open cancels the first.
      resolverRef.current?.({ status: 'cancelled' });

      return new Promise<CreatedByFacilityChoice>((resolve) => {
        resolverRef.current = resolve;
        setPending({
          options: resolution.options,
          defaultId: resolution.defaultId,
          noun,
          summary
        });
      });
    },
    []
  );

  const createdByFacilityDialog = (
    <Dialog
      open={!!pending}
      onClose={() => settle({ status: 'cancelled' })}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Which facility is this {pending?.noun} for?</DialogTitle>
      <DialogContent>
        {pending?.summary && (
          <Typography variant="body2" className="mb-2">
            {pending.summary}
          </Typography>
        )}
        <Typography variant="body2" color="textSecondary" className="mb-3">
          The Task App will list this {pending?.noun} under the facility you pick.
        </Typography>
        <div className="flex flex-col gap-2">
          {pending?.options.map((option) => (
            <Button
              key={option.id}
              variant={String(option.id) === pending.defaultId ? 'contained' : 'outlined'}
              autoFocus={String(option.id) === pending.defaultId}
              onClick={() => settle({ status: 'chosen', facilityId: String(option.id) })}
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
            >
              {facilityDisplayName(option) || `Facility ${option.id}`}
            </Button>
          ))}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => settle({ status: 'cancelled' })}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  return { chooseCreatedByFacility, createdByFacilityDialog };
}
